import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '../../store'
import {
  setClinics,
  setSelectedClinic,
  updateUpcomingAppointments,
} from '../clinicScreen/clinicScreenSlice'
import {
  filterTreatments,
  setSelectedTreatment,
} from '../treatmentScreen/treatmentScreenSlice'
import { setIsRebooking } from '../bookingFlow/bookingFlowSlice'
import {
  getTreatmentsFromCategory,
  setSelectedTreatmentCategory,
} from '../treatmentSelectionSlice'
import { Treatment } from '../../models/types'
import { setNearestAppointmentDate } from '../appointmentScreen/appointmentScreenSlice'
import { Screens } from 'patient-api-lib'
import { fetchClinicsData } from '../appConfigSlice'
import { capitalizeFirstLetter } from '../../util/capitalizeFirstLetter'
import { DateTime } from 'luxon'
import { getLocale } from '../../util/date'

type BookingDetailsScreenState = {
  loader: boolean
}

export const initialState: BookingDetailsScreenState = {
  loader: false,
}

export const done = createAsyncThunk<
  Screens,
  void,
  { dispatch: AppDispatch; state: RootState }
>('bookingDetailsScreen/done', async (_, thunkApi) => {
  await thunkApi.dispatch(fetchClinicsData(true))

  const state = thunkApi.getState()
  const clinics = state.app.clinics
  const selectedBooking = state.yourBookingsScreen.selectedBooking
  const treatmentCategories = state.app.treatmentCategories
  const currentClinic =
    clinics.find((c) => c.id === selectedBooking?.clinicId) || null

  const currentTreatmentCategory = treatmentCategories.find((tc) =>
    tc.treatmentsList.some((t) => t.id === selectedBooking?.treatmentId)
  )

  const currentTreatment = currentTreatmentCategory?.treatmentsList.find(
    (t) => t.id === selectedBooking?.treatmentId
  )

  if (
    currentTreatmentCategory &&
    currentTreatment &&
    clinics &&
    clinics.length > 0
  ) {
    await thunkApi.dispatch(
      setSelectedTreatmentCategory(currentTreatmentCategory)
    )
    thunkApi.dispatch(filterTreatments())
    const categoryTreatments = (await (
      await thunkApi.dispatch(getTreatmentsFromCategory())
    ).payload) as Treatment[]
    const chosenTreatment =
      categoryTreatments.find((t) => t.id === selectedBooking?.treatmentId) ||
      null
    await thunkApi.dispatch(setSelectedTreatment(chosenTreatment))
  }

  if (!currentTreatment) {
    thunkApi.dispatch(setIsRebooking(true))
    thunkApi.dispatch(setSelectedClinic(currentClinic))

    return Screens.TreatmentScreen
  }

  if (!currentClinic || !currentTreatment) {
    return Screens.CityScreen
  }

  thunkApi.dispatch(setClinics([])) // reset clinics in store as side-effect of fetchClinics()
  thunkApi.dispatch(setSelectedClinic(currentClinic))
  thunkApi.dispatch(setIsRebooking(true))
  await thunkApi.dispatch(
    updateUpcomingAppointments({
      clinicId: currentClinic.id,
      pageSize: 1000,
    })
  )
  await thunkApi.dispatch(setNearestAppointmentDate(currentClinic))

  return Screens.AppointmentScreen
})

export const BookingDetailsScreenSlice = createSlice({
  name: 'yourBookingsScreenSlice',
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(done.fulfilled, (state) => {
      state.loader = false
    })
    builder.addCase(done.pending, (state) => {
      state.loader = true
    })
    builder.addCase(done.rejected, (state) => {
      state.loader = false
    })
  },
})

export const selectSummaryFormattedDate = (state: RootState) => {
  const startTime = state.yourBookingsScreen.selectedBooking?.startTime

  if (!startTime) return ''

  const language = state.app.language
  const clockString = language.includes('sv') ? 'klockan' : ''

  return (
    capitalizeFirstLetter(
      DateTime.fromISO(startTime).toFormat(`cccc d LLLL, '${clockString}' `, {
        locale: getLocale(state.app.language),
      })
    ) + DateTime.fromISO(startTime).toFormat('HH:mm')
  )
}

export const getBookingDetailsLoader = (state: RootState) =>
  state.bookingDetailsScreen.loader

export const bookingDetailsSliceReducer = BookingDetailsScreenSlice.reducer
